import { motion } from 'motion/react';
import { Card } from './ui/card';
import { Navigation } from 'lucide-react';

interface WindRoseProps {
  direction: number;
  speed: number;
  gust: number;
}

const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

function getDirectionName(degrees: number): string {
  const index = Math.round(degrees / 45) % 8;
  return directions[index];
}

export function WindRose({ direction, speed, gust }: WindRoseProps) {
  return (
    <Card className="p-6">
      <h3 className="mb-4">Wiatr</h3>
      
      <div className="flex items-center justify-center gap-8">
        <div className="relative w-48 h-48">
          {/* Compass circles */}
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-gray-200"
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute inset-4 rounded-full border border-gray-100"
            animate={{ rotate: -360 }}
            transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute inset-8 rounded-full border border-gray-50"
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          />
          
          {/* Direction labels */}
          {directions.map((dir, idx) => {
            const angle = idx * 45;
            const radian = (angle - 90) * (Math.PI / 180);
            const x = Math.cos(radian) * 85;
            const y = Math.sin(radian) * 85;
            
            return (
              <motion.div
                key={dir}
                className="absolute text-xs text-gray-500"
                style={{
                  left: '50%',
                  top: '50%',
                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                }}
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, delay: idx * 0.1, repeat: Infinity }}
              >
                {dir}
              </motion.div>
            );
          })}
          
          {/* Wind arrow */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            animate={{ rotate: direction }}
            transition={{ duration: 1, ease: 'easeInOut' }}
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <Navigation className="w-12 h-12 text-blue-500 fill-blue-500" />
            </motion.div>
          </motion.div>
        </div>
        
        <div className="flex flex-col gap-4">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col"
          >
            <span className="text-sm text-gray-500">Kierunek</span>
            <span className="text-2xl">{getDirectionName(direction)}</span>
            <span className="text-sm text-gray-400">{direction}°</span>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-col"
          >
            <span className="text-sm text-gray-500">Prędkość</span>
            <span className="text-2xl">{speed.toFixed(2)} km/h</span>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col"
          >
            <span className="text-sm text-gray-500">Podmuchy</span>
            <span className="text-2xl text-orange-500">{gust.toFixed(2)} km/h</span>
          </motion.div>
        </div>
      </div>
    </Card>
  );
}
